import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  LuArrowRight, LuCode, LuSmartphone, LuBrain, LuCloud, 
  LuTrendingUp, LuPalette, LuCircleCheck, LuLayers, LuZap, 
  LuTarget, LuMonitor, LuServer, LuDatabase, LuTestTube,
  LuChevronRight, LuMegaphone, LuRefreshCw
} from 'react-icons/lu';

const serviceAreas = [
  {
    title: 'Product Engineering',
    icon: LuCode,
    items: ['Software Engineering', 'Mobile Engineering', 'Product Development'],
    color: 'blue'
  },
  {
    title: 'Artificial Intelligence',
    icon: LuBrain,
    items: ['AI Development', 'AI As A Service', 'ML Development'],
    color: 'orange'
  },
  {
    title: 'Cloud & DevOps',
    icon: LuCloud,
    items: ['Cloud Consulting', 'DevOps Consulting', 'Infrastructure'],
    color: 'blue'
  },
  {
    title: 'Digital Marketing',
    icon: LuMegaphone,
    items: ['SEO Optimization', 'Social Media', 'Brand Building'],
    color: 'orange'
  },
  {
    title: 'Data Engineering',
    icon: LuDatabase,
    items: ['Data Science', 'Data Analytics', 'Data Governance'],
    color: 'blue'
  },
  {
    title: 'Quality Engineering',
    icon: LuTestTube,
    items: ['Software Testing', 'Test Automation', 'Performance Testing'],
    color: 'orange'
  },
  {
    title: 'App Modernization',
    icon: LuRefreshCw,
    items: ['Legacy Modernization', 'Migration Services', 'Maintenance'],
    color: 'blue'
  },
  {
    title: 'UI/UX Design',
    icon: LuPalette,
    items: ['Product Strategy', 'Experience Design', 'UX Research'],
    color: 'orange'
  }
];

const technologyCategories = [
  {
    id: 'frontend',
    name: 'Frontend',
    icon: LuMonitor,
    techs: ['React', 'Next.js', 'Angular', 'Vue.js', 'TypeScript']
  },
  {
    id: 'backend',
    name: 'Backend',
    icon: LuServer,
    techs: ['Python', 'Node.js', 'Java', '.NET', 'Go']
  },
  {
    id: 'genai',
    name: 'Generative AI',
    icon: LuBrain,
    techs: ['OpenAI', 'Gemini', 'Claude', 'LangChain', 'TensorFlow']
  },
  {
    id: 'cloud',
    name: 'Cloud & DevOps',
    icon: LuCloud,
    techs: ['AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes']
  },
  {
    id: 'mobile',
    name: 'Mobile',
    icon: LuSmartphone,
    techs: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Dart']
  },
  {
    id: 'databases',
    name: 'Databases',
    icon: LuDatabase,
    techs: ['PostgreSQL', 'MongoDB', 'Redis', 'MySQL', 'Firebase']
  }
];

const whyUs = [
  {
    icon: LuCircleCheck,
    title: 'Academic Rigor',
    description: 'Built by engineers with strong academic foundations and research mindset.'
  },
  {
    icon: LuLayers,
    title: 'Clean Architecture',
    description: 'System design-first approach ensuring maintainability and scalability.'
  },
  {
    icon: LuZap,
    title: 'Quality Standards',
    description: 'Industry-grade coding standards and comprehensive testing practices.'
  },
  {
    icon: LuTarget,
    title: 'Long-term Vision',
    description: 'Building solutions that grow with your business, not quick fixes.'
  }
];

const process = [
  { number: '01', title: 'Discover', description: 'Understanding your business needs' },
  { number: '02', title: 'Design', description: 'Architecting scalable solutions' },
  { number: '03', title: 'Build', description: 'Developing with best practices' },
  { number: '04', title: 'Test', description: 'Rigorous quality assurance' },
  { number: '05', title: 'Launch', description: 'Seamless deployment & support' }
];

export default function Home() {
  const [activeTech, setActiveTech] = useState(0);

  return (
    <div className="bg-slate-950" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <motion.div
            animate={{ scale: [1, 1.2, 1], x: [0, 50, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 left-10 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ scale: [1, 1.3, 1], x: [0, -50, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-20 right-10 w-[700px] h-[700px] bg-orange-600/20 rounded-full blur-3xl"
          />
        </div>
        
        <div className="container mx-auto px-6 md:px-8 relative z-10 pt-32 pb-20">
          <div className="max-w-6xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <motion.span 
                className="inline-flex items-center gap-3 bg-blue-500/10 border border-blue-500/30 px-6 py-3 rounded-full text-base font-semibold text-blue-400 backdrop-blur-sm"
                whileHover={{ scale: 1.05 }}
              >
                <motion.span 
                  className="w-2 h-2 bg-blue-400 rounded-full"
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                Where Technology Meets Innovation
              </motion.span>
              
              <motion.h1 
                className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight text-white leading-[1.1]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                Your Complete{' '}
                <span className="text-gradient-blue">Technology</span>
                <br />
                & <span className="text-gradient-orange">Digital Growth</span> Partner
              </motion.h1>
              
              <motion.p 
                className="text-lg sm:text-xl md:text-2xl text-slate-300 leading-relaxed max-w-4xl mx-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                From <span className="text-blue-400 font-semibold">custom software development</span> to{' '}
                <span className="text-orange-400 font-semibold">digital marketing & brand building</span>
                {' '}- we deliver end-to-end solutions that drive real business growth.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6"
              >
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/contact"
                    className="group bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 text-white px-10 py-4 rounded-full text-lg font-bold transition-all shadow-xl shadow-orange-500/40 hover:shadow-orange-500/60 flex items-center space-x-2"
                    data-testid="hero-cta-primary"
                  >
                    <span>Start Your Project</span>
                    <LuArrowRight size={22} />
                  </Link>
                </motion.div>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/services"
                    className="group border-2 border-blue-500/50 bg-blue-500/10 hover:bg-blue-500/20 text-white px-10 py-4 rounded-full text-lg font-bold transition-all backdrop-blur-sm flex items-center space-x-2"
                    data-testid="hero-cta-secondary"
                  >
                    <span>Explore Services</span>
                    <LuArrowRight size={20} />
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-slate-950 to-transparent" />
      </section>

      {/* Services Section - Unique Hexagonal Grid */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-testid="services-section">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
        
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-orange-400 uppercase tracking-widest">What We Do</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mt-4">
              Our <span className="text-gradient-orange">Service Areas</span>
            </h2>
            <p className="text-lg text-slate-400 mt-4 max-w-2xl mx-auto">
              Comprehensive solutions across technology and digital growth
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-6xl mx-auto">
            {serviceAreas.map((area, index) => {
              const Icon = area.icon;
              const isOrange = area.color === 'orange';
              return (
                <motion.div
                  key={area.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05, duration: 0.5 }}
                  className="group"
                >
                  <div className={`relative h-full bg-slate-900/70 backdrop-blur-sm rounded-2xl p-5 md:p-6 border transition-all duration-300 hover:translate-y-[-4px] ${
                    isOrange 
                      ? 'border-orange-500/20 hover:border-orange-500/60 hover:shadow-lg hover:shadow-orange-500/10' 
                      : 'border-blue-500/20 hover:border-blue-500/60 hover:shadow-lg hover:shadow-blue-500/10'
                  }`}>
                    <div className={`absolute top-0 right-0 w-20 h-20 rounded-bl-full opacity-10 ${
                      isOrange ? 'bg-orange-500' : 'bg-blue-500'
                    }`} />
                    
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${
                      isOrange 
                        ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/30' 
                        : 'bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30'
                    }`}>
                      <Icon size={22} className={isOrange ? 'text-orange-400' : 'text-blue-400'} />
                    </div>
                    
                    <h3 className={`font-heading text-base md:text-lg font-semibold text-white mb-3 transition-colors ${
                      isOrange ? 'group-hover:text-orange-400' : 'group-hover:text-blue-400'
                    }`}>
                      {area.title}
                    </h3>
                    
                    <ul className="space-y-1.5">
                      {area.items.map((item, i) => (
                        <li key={i} className="text-slate-400 text-xs md:text-sm flex items-center gap-2">
                          <span className={`w-1 h-1 rounded-full ${isOrange ? 'bg-orange-500/50' : 'bg-blue-500/50'}`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold transition-colors group"
            >
              View All Services
              <LuChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Technologies Section - Interactive Tabs */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-900/50 to-slate-950" data-testid="tech-section">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-sm font-medium text-blue-400 uppercase tracking-widest">Tech Stack</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mt-4">
              Technologies We <span className="text-gradient-blue">Master</span>
            </h2>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            {/* Tab Headers */}
            <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8">
              {technologyCategories.map((cat, index) => {
                const Icon = cat.icon;
                const isActive = activeTech === index;
                return (
                  <motion.button
                    key={cat.id}
                    onClick={() => setActiveTech(index)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/30'
                        : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 hover:text-white border border-slate-700'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Icon size={16} />
                    <span className="hidden sm:inline">{cat.name}</span>
                  </motion.button>
                );
              })}
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTech}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 md:p-10"
              >
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      {(() => {
                        const Icon = technologyCategories[activeTech].icon;
                        return (
                          <div className="w-12 h-12 rounded-xl bg-orange-500/20 border border-orange-500/40 flex items-center justify-center">
                            <Icon size={24} className="text-orange-400" />
                          </div>
                        );
                      })()}
                      <h3 className="font-heading text-2xl md:text-3xl font-bold text-white">
                        {technologyCategories[activeTech].name}
                      </h3>
                    </div>
                    <p className="text-slate-400 mb-6">
                      Expert solutions using industry-leading {technologyCategories[activeTech].name.toLowerCase()} technologies
                    </p>
                    <Link
                      to={`/technologies/${technologyCategories[activeTech].id}`}
                      className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold transition-colors group"
                    >
                      Explore {technologyCategories[activeTech].name}
                      <LuArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                  
                  <div className="flex flex-wrap gap-3 justify-center md:justify-end">
                    {technologyCategories[activeTech].techs.map((tech, i) => (
                      <motion.div
                        key={tech}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.05 }}
                        className="px-4 py-2 bg-slate-800/70 rounded-full border border-slate-700 text-sm text-slate-300 hover:border-blue-500/50 hover:text-white transition-all"
                      >
                        {tech}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-center mt-8"
            >
              <Link
                to="/technologies"
                className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 px-6 py-3 rounded-full text-blue-400 hover:bg-blue-500/20 font-semibold transition-all"
              >
                View All Technologies
                <LuChevronRight size={18} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-20 md:py-28" data-testid="why-us-section">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-orange-400 uppercase tracking-widest">Why TechMeetsU</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mt-4">
              Engineering Principles Over <span className="text-gradient-orange">Shortcuts</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {whyUs.map((item, index) => {
              const Icon = item.icon;
              const isOrange = index % 2 !== 0;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="text-center space-y-4 group"
                  data-testid={`why-us-card-${index}`}
                >
                  <motion.div 
                    className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center border-2 transition-all group-hover:scale-110 ${
                      isOrange 
                        ? 'bg-orange-600/10 border-orange-500/30 group-hover:border-orange-500/60' 
                        : 'bg-blue-600/10 border-blue-500/30 group-hover:border-blue-500/60'
                    }`}
                  >
                    <Icon className={isOrange ? 'text-orange-400' : 'text-blue-400'} size={28} />
                  </motion.div>
                  <h3 className="font-heading text-xl font-semibold text-white">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section - Timeline Style */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-900/30 via-orange-950/10 to-slate-900/30" data-testid="process-section">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-blue-400 uppercase tracking-widest">Our Process</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mt-4">
              Structured Approach to <span className="text-gradient-blue">Excellence</span>
            </h2>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500/50 via-orange-500/50 to-blue-500/50 -translate-y-1/2" />
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 relative">
                {process.map((step, index) => {
                  const isOrange = index % 2 !== 0;
                  return (
                    <motion.div
                      key={step.number}
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1, duration: 0.6 }}
                      className="relative"
                      data-testid={`process-step-${index}`}
                    >
                      {/* Circle on timeline */}
                      <div className={`hidden md:flex absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 z-10 ${
                        isOrange ? 'bg-orange-500 border-orange-400' : 'bg-blue-500 border-blue-400'
                      }`} />
                      
                      <div className={`bg-slate-900/70 border rounded-2xl p-5 transition-all hover:translate-y-[-4px] ${
                        isOrange 
                          ? 'border-orange-500/20 hover:border-orange-500/50' 
                          : 'border-blue-500/20 hover:border-blue-500/50'
                      }`}>
                        <div className={`text-4xl font-heading font-bold mb-3 ${
                          isOrange ? 'text-gradient-orange' : 'text-gradient-blue'
                        }`}>{step.number}</div>
                        <h3 className="font-heading text-lg font-semibold text-white mb-1">{step.title}</h3>
                        <p className="text-slate-400 text-sm">{step.description}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 orange-glow" />
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center space-y-8"
          >
            <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-white leading-tight">
              Ready to Build Something <span className="text-gradient-orange">Exceptional?</span>
            </h2>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl mx-auto">
              Let's discuss how we can help bring your vision to life with engineering excellence and scalable architecture.
            </p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-10 py-5 rounded-full text-lg font-bold transition-all shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 group"
                data-testid="cta-button"
              >
                <span>Start Your Project</span>
                <LuArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
